import { ChevronsLeftIcon, ChevronsRightIcon } from 'lucide-react'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import { useCallback, useContext, useRef, useState } from 'react'
import { NavLink } from 'react-router'

import { useEntityAccess } from '@/hooks/useEntityAccess'
import { usePublishedFaqs } from '@/hooks/usePublishedFaqs'
import { router } from '@/routes'
import { UserStoreContext } from '@/stores/UserStore'
import { cn } from '@/utils/helpers'
import { getNavItems } from '@/utils/routes'

const SHORT_LABELS = {
  'Emergency Action Plans': 'EAPs',
  'Account Management': 'Account',
  'External Credential Categories': 'Creds',
}

/**
 * Flattens nav items into bottom-nav tabs.
 * Groups expand into individual items (each sub-item becomes its own tab).
 * Non-group items without icons are skipped.
 */
function flattenNavItems(navItems) {
  return navItems.reduce((result, item) => {
    if (item.isGroup && item.items?.length > 0) {
      item.items.forEach((subItem) => {
        result.push({
          title: SHORT_LABELS[subItem.title] || subItem.title,
          icon: subItem.icon || item.icon,
          url: subItem.url,
        })
      })
    } else if (item.isGroup && item.icon) {
      // Group with no showInNav children (e.g. Drill Timer) — link directly
      result.push({
        title: SHORT_LABELS[item.title] || item.title,
        icon: item.icon,
        url: item.url,
      })
    } else if (item.icon) {
      result.push({
        title: SHORT_LABELS[item.title] || item.title,
        icon: item.icon,
        url: item.url,
      })
    }
    return result
  }, [])
}

const BottomNavItem = ({ item }) => (
  <NavLink to={item.url} className="shrink-0">
    {({ isActive }) => (
      <div
        className={cn(
          'flex flex-col items-center gap-0.5 px-3 py-2 text-xs whitespace-nowrap',
          isActive ? 'text-sidebar-accent-foreground font-medium' : 'text-sidebar-foreground/60',
        )}
      >
        {item.icon && <item.icon className="size-5" />}
        <span>{item.title}</span>
      </div>
    )}
  </NavLink>
)

BottomNavItem.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string.isRequired,
    icon: PropTypes.elementType,
    url: PropTypes.string.isRequired,
  }).isRequired,
}

// Persists across remounts (Layout recreates BottomNav on every route change)
let persistedScrollLeft = 0
let persistedCanScrollLeft = false
let persistedCanScrollRight = false

const BottomNav = observer(() => {
  const { activeEntity, isXverseAdmin, isXverseInstructorCandidate, user } =
    useContext(UserStoreContext)
  const { entityAccess, isEntityAccessResolved } = useEntityAccess(activeEntity)
  const { hasPublishedFaqs } = usePublishedFaqs({ enabled: !!user?.id && !!activeEntity })
  const scrollRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(persistedCanScrollLeft)
  const [canScrollRight, setCanScrollRight] = useState(persistedCanScrollRight)

  const checkScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const left = el.scrollLeft > 2
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2
    persistedCanScrollLeft = left
    persistedCanScrollRight = right
    setCanScrollLeft(left)
    setCanScrollRight(right)
  }, [])

  const handleScroll = useCallback(() => {
    if (scrollRef.current) {
      persistedScrollLeft = scrollRef.current.scrollLeft
    }
    checkScroll()
  }, [checkScroll])

  // Ref callback: restore scroll position, attach/cleanup listener
  const setRef = useCallback(
    (node) => {
      // Cleanup previous node
      if (scrollRef.current && scrollRef.current !== node) {
        scrollRef.current.removeEventListener('scroll', handleScroll)
      }
      scrollRef.current = node
      if (node) {
        Object.assign(node, { scrollLeft: persistedScrollLeft })
        node.addEventListener('scroll', handleScroll, { passive: true })
        requestAnimationFrame(checkScroll)
      }
    },
    [handleScroll, checkScroll],
  )

  const adminRoutes = router.routes.find((r) => r.id === 'AdminRoutes')
  const entityRoutes = router.routes.find((r) => r.id === 'EntityRoutes')

  const sharedProps = {
    activeEntity,
    roles: user?.entityRoles,
    isXverseAdmin,
    isXverseInstructorCandidate,
    entityAccess,
    entityAccessResolved: isEntityAccessResolved,
    hasPublishedFaqs,
  }

  let rawItems = []
  if (!activeEntity && isXverseAdmin && adminRoutes) {
    rawItems = getNavItems(adminRoutes, sharedProps)
  } else if (activeEntity && entityRoutes) {
    rawItems = getNavItems(entityRoutes, sharedProps)
  }

  const tabs = flattenNavItems(rawItems)

  if (tabs.length === 0) return null

  return (
    <nav className="bg-sidebar text-sidebar-foreground border-sidebar-border safe-area-pb fixed inset-x-0 bottom-0 z-50 border-t md:hidden">
      <div className="relative flex">
        {/* Left scroll indicator */}
        <div
          className={cn(
            'bg-sidebar pointer-events-none absolute top-0 bottom-0 left-0 z-10 flex w-5 items-center justify-center transition-opacity duration-150',
            canScrollLeft ? 'opacity-100' : 'opacity-0',
          )}
        >
          <ChevronsLeftIcon className="text-sidebar-foreground size-5" />
        </div>
        <div ref={setRef} className="scrollbar-none flex overflow-x-auto overscroll-contain">
          {tabs.map((item) => (
            <BottomNavItem key={item.url} item={item} />
          ))}
        </div>
        {/* Right scroll indicator */}
        <div
          className={cn(
            'bg-sidebar pointer-events-none absolute top-0 right-0 bottom-0 z-10 flex w-5 items-center justify-center transition-opacity duration-150',
            canScrollRight ? 'opacity-100' : 'opacity-0',
          )}
        >
          <ChevronsRightIcon className="text-sidebar-foreground size-5" />
        </div>
      </div>
    </nav>
  )
})

export default BottomNav
